import { api } from './client';

interface PublicLinkInfo {
  token: string;
  linkType: 'download' | 'upload';
  fileName?: string;
  folderName?: string;
  hasPassword: boolean;
  expiresAt: string | null;
  isExpired: boolean;
  remainingDownloads: number | null;
  remainingUploads: number | null;
}

interface PublicLink {
  id: string;
  linkType: 'download' | 'upload';
  token: string;
  fileId: string | null;
  folderId: string | null;
  fileName?: string;
  folderName?: string;
  hasPassword: boolean;
  maxDownloads: number | null;
  currentDownloads: number;
  maxUploads: number | null;
  currentUploads: number;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  lastAccessedAt: string | null;
}

interface CreateLinkResponse {
  link: {
    id: string;
    token: string;
    linkType: 'download' | 'upload';
    url: string;
  };
}

export const publicLinksApi = {
  // Public (no auth)
  getInfo: (token: string) => api.get<PublicLinkInfo>(`/public/${token}`),

  download: (token: string, password?: string) => {
    const params = password ? `?password=${encodeURIComponent(password)}` : '';
    return api.get<{ downloadUrl: string; fileName: string }>(
      `/public/${token}/download${params}`
    );
  },

  upload: (
    token: string,
    data: {
      fileName: string;
      mimeType: string;
      size: number;
      password?: string;
    }
  ) =>
    api.post<{ uploadUrl: string; fileId: string; r2Key: string }>(
      `/public/${token}/upload`,
      data
    ),

  confirmUpload: (token: string, fileId: string) =>
    api.post<{ success: boolean }>(`/public/${token}/upload/confirm`, { fileId }),

  // Authenticated
  list: () => api.get<{ links: PublicLink[] }>('/links'),

  create: (data: {
    fileId?: string;
    folderId?: string;
    linkType: 'download' | 'upload';
    password?: string;
    maxDownloads?: number;
    maxUploads?: number;
    maxFileSize?: number;
    allowedExtensions?: string[];
    expiresAt?: string;
  }) => api.post<CreateLinkResponse>('/links', data),

  update: (
    id: string,
    data: {
      isActive?: boolean;
      expiresAt?: string | null;
      maxDownloads?: number | null;
      maxUploads?: number | null;
    }
  ) => api.patch<{ success: boolean }>(`/links/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean }>(`/links/${id}`),

  getStats: (id: string) =>
    api.get<{
      totalDownloads: number;
      totalUploads: number;
      recentAccess: Array<{
        id: string;
        action: string;
        ipAddress: string | null;
        createdAt: string;
      }>;
    }>(`/links/${id}/stats`),
};
